import type {
  AccountingChartQuery,
  AccountingJournalQuery,
  AccountingLedgerQuery,
  AccountingPeriodQuery,
} from "@/features/finance/model/accounting-reports-types"
import type {
  BudgetLineMatchQuery,
  FinanceActivitiesListQuery,
  FinanceCurrentIncomeBalanceQuery,
  FinanceCurrentIncomeBalancesQuery,
  FinanceOverviewQuery,
  FinanceTransactionListQuery,
  FinanceTransferRequestListQuery,
  ListFinanceAccountsQuery,
  ListFinanceFundsQuery,
  ListParishTransactionsQuery,
} from "@/features/finance/model/finance-types"
import type {
  ListReconciliationSessionsQuery,
  OpeningBalancesQuery,
} from "@/features/finance/model/finance-close-trust-types"

/**
 * The finance feature's query keys (ADR-0007 / `docs/rules/query-keys.rules.md`), typed and built in ONE place.
 *
 * IBA-160: every finance hook used to spell its own literal key inline (`["finance", "accounts", query]`), and
 * every write invalidated the bare `["finance"]` root — so recording a transaction refetched the chart of
 * accounts, the fiscal calendar and the receipt config too. Keys now come from this factory and writes
 * invalidate the specific sub-trees they actually change (see `finance-query.ts`).
 *
 * Every key is workspace/branch-scoped through the query object it is built from — no broad workspace or
 * branch-wide key exists here.
 */

/** Stands in for an absent scope segment so `undefined` and `null` cannot collapse two different queries. */
const EMPTY = "__none__"

const scalar = (value: string | number | boolean | null | undefined) => value ?? EMPTY

export const financeQueryKeys = {
  all: ["finance"] as const,

  /* --- financial accounts (branch-owned balances) --- */
  accounts: () => [...financeQueryKeys.all, "accounts"] as const,
  /** The supported "held as" account types — backend-owned enum metadata (static across the session). */
  accountTypes: () => [...financeQueryKeys.all, "account-types"] as const,
  accountList: (query: ListFinanceAccountsQuery | null) =>
    [...financeQueryKeys.accounts(), "list", query] as const,

  /* --- funds (append-only allocation ledger) --- */
  funds: () => [...financeQueryKeys.all, "funds"] as const,
  fundList: (query: ListFinanceFundsQuery | null) => [...financeQueryKeys.funds(), "list", query] as const,

  /** Saved transfer calculations, scoped to the branch that owns them. */
  savedTransferCalculations: (workspaceId: string, branchId: string) =>
    [...financeQueryKeys.all, "transfers", "saved-calculations", workspaceId, branchId] as const,

  /* --- chart of accounts / journal / ledger (read-only, derived from the journal) --- */
  accounting: () => [...financeQueryKeys.all, "accounting"] as const,
  accountingChart: (query: AccountingChartQuery | null) =>
    [...financeQueryKeys.accounting(), "chart", query] as const,
  accountingJournal: (query: AccountingJournalQuery | null) =>
    [...financeQueryKeys.accounting(), "journal", query] as const,
  accountingJournalInfinite: (query: AccountingJournalQuery | null) =>
    [...financeQueryKeys.accounting(), "journal", "infinite", query] as const,
  accountingLedger: (query: AccountingLedgerQuery | null) =>
    [...financeQueryKeys.accounting(), "ledger", query] as const,
  accountingTrialBalance: (query: AccountingPeriodQuery | null) =>
    [...financeQueryKeys.accounting(), "trial-balance", query] as const,
  accountingStatements: (query: AccountingPeriodQuery | null) =>
    [...financeQueryKeys.accounting(), "statements", query] as const,

  /* --- transactions (the operator-facing ledger) --- */
  transactions: () => [...financeQueryKeys.all, "transactions"] as const,
  transactionList: (query: FinanceTransactionListQuery | null) =>
    [...financeQueryKeys.transactions(), "list", query] as const,
  transactionPage: (query: FinanceTransactionListQuery | null) =>
    [...financeQueryKeys.transactions(), "page", query] as const,
  parishTransactionList: (query: ListParishTransactionsQuery | null) =>
    [...financeQueryKeys.transactions(), "parish", query] as const,

  /**
   * Categories root — retained only as an invalidation target for `invalidateFinanceDirectory`. The
   * income/expense category models were retired (categories→accounts convergence); no list/page keys remain.
   */
  categories: () => [...financeQueryKeys.all, "categories"] as const,

  /* --- activities --- */
  activities: () => [...financeQueryKeys.all, "activities"] as const,
  activityList: (query: FinanceActivitiesListQuery | null) =>
    [...financeQueryKeys.activities(), "list", query] as const,

  /* --- overview / balances / health (projections of the ledger) --- */
  overview: () => [...financeQueryKeys.all, "overview"] as const,
  overviewSummary: (query: FinanceOverviewQuery | null) => [...financeQueryKeys.overview(), query] as const,
  health: (query: AccountingPeriodQuery | null) => [...financeQueryKeys.overview(), "health", query] as const,
  /** The church-wide board's own figures (`GET /finance/overview/church`): workspace and period, never broader. */
  churchOverview: (query: { workspaceId: string | null | undefined; dateFrom: string; dateTo: string }) =>
    [
      ...financeQueryKeys.overview(),
      "church",
      scalar(query.workspaceId),
      { dateFrom: query.dateFrom, dateTo: query.dateTo },
    ] as const,
  /** The branch board's own figures (`GET /finance/overview/branch`): workspace, branch and period, never broader. */
  branchBoard: (query: { workspaceId: string | null | undefined; branchId: string | null | undefined; dateFrom: string; dateTo: string }) =>
    [
      ...financeQueryKeys.overview(),
      "branch-board",
      scalar(query.workspaceId),
      scalar(query.branchId),
      { dateFrom: query.dateFrom, dateTo: query.dateTo },
    ] as const,
  /** The branch board's treasury (`GET /finance/overview/home` with its own week count). */
  branchTreasury: (query: {
    workspaceId: string | null | undefined
    branchId: string | null | undefined
    dateFrom: string
    dateTo: string
    weeks: number
  }) =>
    [
      ...financeQueryKeys.overview(),
      "branch-treasury",
      scalar(query.workspaceId),
      scalar(query.branchId),
      { dateFrom: query.dateFrom, dateTo: query.dateTo, weeks: query.weeks },
    ] as const,
  balances: () => [...financeQueryKeys.all, "balances"] as const,
  currentIncomeBalance: (query: FinanceCurrentIncomeBalanceQuery | null) =>
    [...financeQueryKeys.balances(), "current-income", "one", query] as const,
  currentIncomeBalances: (query: FinanceCurrentIncomeBalancesQuery | null) =>
    [...financeQueryKeys.balances(), "current-income", "list", query] as const,

  /* --- allocation-aware budget impacts: every financial input is part of cache identity --- */
  budgetLineMatches: (query: BudgetLineMatchQuery | null) =>
    [
      ...financeQueryKeys.all,
      "budget-line-matches",
      scalar(query?.workspaceId),
      scalar(query?.branchId),
      scalar(query?.type),
      scalar(query?.accountingAccountId),
      scalar(query?.amount),
      scalar(query?.fundId),
      query?.fundAllocations?.map((allocation) => [allocation.fundId, allocation.amount] as const) ?? null,
      scalar(query?.occurredAt),
    ] as const,

  /* --- receipts --- */
  receiptConfigs: () => [...financeQueryKeys.all, "receipt-config"] as const,
  receiptConfig: (input: { workspaceId: string; branchId: string } | null) =>
    [...financeQueryKeys.receiptConfigs(), input] as const,

  /* --- close & trust: fiscal periods, opening balances, reconciliation (IBA-71/72/73) --- */
  fiscal: () => [...financeQueryKeys.all, "fiscal"] as const,
  fiscalCalendar: (workspaceId: string | null, branchId: string | null) =>
    // The branch is part of the key, not decoration: two branches have different calendars, and a key that
    // omitted the branch would serve one branch's months to the other.
    [...financeQueryKeys.fiscal(), "calendar", scalar(workspaceId), scalar(branchId)] as const,
  openingBalances: () => [...financeQueryKeys.all, "opening-balances"] as const,
  openingBalancesState: (query: OpeningBalancesQuery | null) =>
    [...financeQueryKeys.openingBalances(), query] as const,
  /**
   * The opening FUND ALLOCATION (IBA-170) — what the opening money is FOR, as opposed to where it is.
   * Branch-scoped for the usual reason: two branches divide their own money, and a key that omitted the
   * branch would serve one branch's split to the other.
   */
  openingFundAllocations: () => [...financeQueryKeys.all, "opening-fund-allocations"] as const,
  openingFundAllocationsState: (workspaceId: string | null, branchId: string | null) =>
    [...financeQueryKeys.openingFundAllocations(), scalar(workspaceId), scalar(branchId)] as const,
  reconciliation: () => [...financeQueryKeys.all, "reconciliation"] as const,
  reconciliationSessions: (query: ListReconciliationSessionsQuery | null) =>
    [...financeQueryKeys.reconciliation(), "sessions", query] as const,
  reconciliationSession: (input: { workspaceId: string; sessionId: string } | null) =>
    [...financeQueryKeys.reconciliation(), "session", input] as const,

  /**
   * Cross-context transfer REQUESTS (IBA-158) — their own sub-tree, not under `transactions`, because a
   * pending request is not a transaction: it has posted nothing. Approving one DOES post, which is why
   * `invalidateTransferRequests` also takes everything a posted fact moves.
   */
  transferRequests: () => [...financeQueryKeys.all, "transfer-requests"] as const,
  transferRequestList: (query: FinanceTransferRequestListQuery | null) =>
    [...financeQueryKeys.transferRequests(), "list", query] as const,
  transferRequestDetail: (id: string | null) =>
    [...financeQueryKeys.transferRequests(), "detail", id] as const,
  /** Scoped by the SENDING branch: what one branch may address money to is not what another may. */
  transferDestinations: (workspaceId: string | null, sourceBranchId: string | null) =>
    [...financeQueryKeys.transferRequests(), "destinations", workspaceId, sourceBranchId] as const,

  /* --- audit trail / investigations (read-only views over recorded finance events) --- */
  audit: () => [...financeQueryKeys.all, "audit"] as const,
  investigation: () => [...financeQueryKeys.all, "investigation"] as const,

  /**
   * Branch finance setup status — the module's UNLOCK GATE. `isMinimumSetupComplete` drives the setup guard,
   * the nav gate, the "can record" toolbar and the onboarding wizard. Its payload carries
   * `financialAccountsCount` and `postedEntries`, so any write that creates the first account or posts an
   * entry MUST invalidate it — or the treasurer finishes the wizard and the module still says it is not set up.
   */
  setup: () => [...financeQueryKeys.all, "setup"] as const,
  setupStatus: (workspaceId: string | null, branchId: string | null) =>
    [...financeQueryKeys.setup(), scalar(workspaceId), scalar(branchId)] as const,
  /**
   * What choosing BRANCH_AUTONOMY would take away (ADR-0024). Workspace-scoped, not branch-scoped: the
   * governance question is org-wide, and the answer names the branches the parish would stop seeing.
   */
  governanceImpact: (workspaceId: string | null) =>
    [...financeQueryKeys.setup(), "governance-impact", scalar(workspaceId)] as const,
  /**
   * Wizard progress is UX state, not finance ledger data. Keep it outside the broad finance root so setup,
   * account, fund, and transaction writes cannot refetch stale progress in the middle of a step transition.
   */
  onboarding: () => ["finance-onboarding-progress"] as const,
  onboardingProgress: (workspaceId: string | null, branchId: string | null) =>
    [...financeQueryKeys.onboarding(), scalar(workspaceId), scalar(branchId)] as const,
  branchCurrency: (branchId: string | null) =>
    [...financeQueryKeys.all, "branch-currency", scalar(branchId)] as const,
}
